import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { Info } from "lucide-react";
import { ReactComponent as Network } from 'assets/icon-network.svg';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "config/axiosConfig";
import EntityActions from "components/Entities/EntityActions";
import EntityAvatar from "components/Entities/EntityAvatar";
import IssueSearch from "components/Issues/IssueSearch";
import NotFoundPage from "components/ResponsePages/404NotFoundPage";
import ServerErrorPage from "components/ResponsePages/500ServerErrorPage";
import { Carousel, CarouselContent, CarouselItem } from "ui/Carousel";
import AddButton from "ui/AddButton";
import { Drawer, DrawerContent } from "ui/Drawer";
import EmptyState from "ui/EmptyState";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { Issue, Suggestion } from "util/constants";
import { useUser } from "util/UserContext";
import { createIssueFromWikipedia, createCompoundIssue } from "util/issueCreationUtils";
import { getWikipediaSummary } from "util/wikiUtils";
import { toast } from "sonner";
import { useDrawerNavigation } from "ui/DrawerStack";
import { apiRoutes } from "util/apiRoutes";
import { logger } from "lib/logger";

interface IssueInfluenceSurfaceProps {
  /** When set, overrides the route-param cname (used by DrawerStack). */
  stackCname?: string;
  /** When set, the component renders in stacked-drawer mode (no own Drawer wrapper). */
  onStackClose?: () => void;
}

interface InfluencesResponse {
  influences?: Issue[];
  total?: number;
}

interface InfluenceCardData {
  compoundIssue: Issue;
  standaloneIssue: Issue;
}

type InfluenceDirection = "influencer" | "influenced";

/**
 * Renders a standalone-issue influence explorer with a center issue
 * and carousels for influencers (top) and influenced (bottom).
 */
const IssueInfluenceSurface: React.FC<IssueInfluenceSurfaceProps> = ({ stackCname, onStackClose }) => {
  const { cname: paramCname } = useParams<{ cname: string }>();
  const cname = stackCname || paramCname;
  const isStacked = Boolean(onStackClose);
  const location = useLocation();
  const navigate = useNavigate();
  const { userIsMobile, currentUser, setShowAuthModal } = useUser();
  const { openIssueFeed, openIssueInfluences } = useDrawerNavigation();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(true);
  const [currentIssue, setCurrentIssue] = useState<Issue | null>(null);
  const [influencerIssues, setInfluencerIssues] = useState<Issue[]>([]);
  const [influencedIssues, setInfluencedIssues] = useState<Issue[]>([]);
  const [isIssueLoading, setIsIssueLoading] = useState<boolean>(true);
  const [isInfluencersLoading, setIsInfluencersLoading] = useState<boolean>(true);
  const [isInfluencedLoading, setIsInfluencedLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [centerIssueExtract, setCenterIssueExtract] = useState<string>("");

  const [selectedInfluencer, setSelectedInfluencer] = useState<Suggestion | null>(null);
  const [selectedInfluenced, setSelectedInfluenced] = useState<Suggestion | null>(null);
  const [isSubmittingInfluencer, setIsSubmittingInfluencer] = useState(false);
  const [isSubmittingInfluenced, setIsSubmittingInfluenced] = useState(false);
  const [actionMessageInfluencer, setActionMessageInfluencer] = useState<string | null>(null);
  const [actionMessageInfluenced, setActionMessageInfluenced] = useState<string | null>(null);

  const fetchIssue = useCallback(async () => {
    setIsIssueLoading(true);
    setError("");

    try {
      try {
        if (!cname) {
          throw new Error("Missing issue cname");
        }

        const response = await api.get<Issue>(apiRoutes.issues.byName(encodeURIComponent(cname)));
        setCurrentIssue(response.data);
        return;
      } catch (nameError) {
        if (!Number.isNaN(Number(cname))) {
          const idResponse = await api.get<Issue>(apiRoutes.issues.byId(cname as string));
          const canonicalIssue = idResponse.data;
          const newPath = `/issue/${encodeURIComponent(canonicalIssue.cname)}/influences`;
          navigate(`${newPath}${location.search}`, { replace: true });
          return;
        }

        throw nameError;
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) {
        setError("404: Failed to load influence explorer.");
      } else {
        setError("Failed to load influence explorer.");
      }
    } finally {
      setIsIssueLoading(false);
    }
  }, [cname, location.search, navigate]);

  const fetchInfluences = useCallback(async (issueId: number) => {
    setIsInfluencersLoading(true);
    setIsInfluencedLoading(true);

    try {
      const [influencerResponse, influencedResponse] = await Promise.all([
        api.get<InfluencesResponse>(apiRoutes.issues.influences(issueId), {
          params: { type: "influencer", limit: 50 },
        }),
        api.get<InfluencesResponse>(apiRoutes.issues.influences(issueId), {
          params: { type: "influenced", limit: 50 },
        }),
      ]);

      setInfluencerIssues(Array.isArray(influencerResponse.data?.influences) ? influencerResponse.data.influences : []);
      setInfluencedIssues(Array.isArray(influencedResponse.data?.influences) ? influencedResponse.data.influences : []);
    } catch (err) {
      setError("Failed to load issue influences.");
    } finally {
      setIsInfluencersLoading(false);
      setIsInfluencedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIssue();
  }, [fetchIssue]);

  useEffect(() => {
    if (!currentIssue?.id) {
      return;
    }
    fetchInfluences(currentIssue.id);
  }, [currentIssue?.id, fetchInfluences]);

  useEffect(() => {
    const fetchCenterIssueExtract = async () => {
      if (!currentIssue) {
        setCenterIssueExtract("");
        return;
      }

      const fallbackExtract = String(
        currentIssue.properties?.subject_properties?.extract ||
        currentIssue.properties?.wiki_summary ||
        ""
      );

      try {
        const summary = await getWikipediaSummary(currentIssue.cname);
        setCenterIssueExtract(summary?.extract || fallbackExtract);
      } catch (err) {
        setCenterIssueExtract(fallbackExtract);
      }
    };

    fetchCenterIssueExtract();
  }, [currentIssue]);

  const buildInfluenceCardData = useCallback(
    (compoundIssue: Issue, direction: InfluenceDirection): InfluenceCardData => {
      const standaloneName =
        direction === "influencer"
          ? (compoundIssue.subject_cname || "").replace(/_/g, " ")
          : (compoundIssue.topic_cname || "").replace(/_/g, " ");

      const standaloneCname =
        direction === "influencer" ? compoundIssue.subject_cname : compoundIssue.topic_cname;

      const standaloneProperties =
        direction === "influencer"
          ? compoundIssue.properties?.subject_properties
          : compoundIssue.properties?.topic_properties;

      const standaloneIssue: Issue = {
        ...compoundIssue,
        id: direction === "influencer" ? compoundIssue.subject_id : compoundIssue.topic_id,
        name: standaloneName || "Unknown issue",
        cname: standaloneCname || compoundIssue.cname,
        topic_id: 0,
        topic_cname: "",
        properties: standaloneProperties || {},
      };

      return {
        compoundIssue,
        standaloneIssue,
      };
    },
    []
  );

  const influencerCards = useMemo(
    () => influencerIssues.map((influence) => buildInfluenceCardData(influence, "influencer")),
    [buildInfluenceCardData, influencerIssues]
  );

  const influencedCards = useMemo(
    () => influencedIssues.map((influence) => buildInfluenceCardData(influence, "influenced")),
    [buildInfluenceCardData, influencedIssues]
  );

  /** Open the IssueFeedSurface (not full profile) for a compound/influence issue. */
  const handleOpenCompoundIssue = (compoundIssue: Issue) => {
    openIssueFeed(compoundIssue.cname);
  };

  /** Open a new influence explorer for the given issue (pushes stack on mobile). */
  const handleRecenterIssue = (standaloneIssue: Issue) => {
    openIssueInfluences(standaloneIssue.cname);
  };

  const getWikipediaLink = (issue: Issue | Suggestion): string => {
    const properties = (issue as Issue).properties;
    if (properties?.is_custom_issue || properties?.subject_properties?.is_custom_issue) {
      return "";
    }
    if ("wikipedia_link" in issue && issue.wikipedia_link) return issue.wikipedia_link;
    if ("topic_wikipedia_link" in issue && issue.topic_wikipedia_link) return issue.topic_wikipedia_link;
    if ("cname" in issue && issue.cname) {
      const title = (issue.cname as string).replace(/_/g, " ");
      return `https://en.wikipedia.org/wiki/${encodeURIComponent(title)}`;
    }
    return "";
  };

  const getExcludedInfluencerIds = (): number[] => {
    const base = currentIssue?.id ?? 0;
    const subjectIds = influencerIssues.map((i) => i.subject_id).filter((id): id is number => typeof id === "number");
    return [base, ...subjectIds].filter((id) => id !== 0);
  };

  const getExcludedInfluencedIds = (): number[] => {
    const base = currentIssue?.id ?? 0;
    const topicIds = influencedIssues.map((i) => i.topic_id).filter((id): id is number => typeof id === "number");
    return [base, ...topicIds].filter((id) => id !== 0);
  };

  const handleAddInfluencer = async () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!selectedInfluencer || !currentIssue) {
      setActionMessageInfluencer("Please select an issue first");
      return;
    }
    setIsSubmittingInfluencer(true);
    setActionMessageInfluencer(null);
    try {
      let issueToLink = selectedInfluencer;
      if (selectedInfluencer.type === "wiki") {
        const created = await createIssueFromWikipedia(selectedInfluencer);
        issueToLink = { ...created, type: "issue" as const };
      }
      await createCompoundIssue(
        (issueToLink as Issue).cname || "",
        currentIssue.cname,
        getWikipediaLink(issueToLink),
        getWikipediaLink(currentIssue),
        (issueToLink as Issue).properties?.subject_properties,
        currentIssue.properties?.subject_properties
      );
      await fetchInfluences(currentIssue.id);
      setSelectedInfluencer(null);
      toast.success("Influence relationship added successfully");
    } catch (err) {
      logger.error("Error adding influencer relation", { err });
      setActionMessageInfluencer("Couldn't create the influence relationship. Make sure this isn't already an influence");
    } finally {
      setIsSubmittingInfluencer(false);
    }
  };

  const handleAddInfluenced = async () => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!selectedInfluenced || !currentIssue) {
      setActionMessageInfluenced("Please select an issue first");
      return;
    }
    setIsSubmittingInfluenced(true);
    setActionMessageInfluenced(null);
    try {
      let issueToLink = selectedInfluenced;
      if (selectedInfluenced.type === "wiki") {
        const created = await createIssueFromWikipedia(selectedInfluenced);
        issueToLink = { ...created, type: "issue" as const };
      }
      await createCompoundIssue(
        currentIssue.cname,
        (issueToLink as Issue).cname || "",
        getWikipediaLink(currentIssue),
        getWikipediaLink(issueToLink),
        currentIssue.properties?.subject_properties,
        (issueToLink as Issue).properties?.subject_properties
      );
      await fetchInfluences(currentIssue.id);
      setSelectedInfluenced(null);
      toast.success("Influence relationship added successfully");
    } catch (err) {
      logger.error("Error adding influenced relation", { err });
      setActionMessageInfluenced("Couldn't create the influence relationship. Make sure this isn't already an influence");
    } finally {
      setIsSubmittingInfluenced(false);
    }
  };

  const renderLoadingCarousel = (prefix: string) => (
    <Carousel
      className="issue-influence-surface-carousel w-full"
      opts={{ dragFree: true, align: "center" }}
    >
      <CarouselContent className="issue-influence-surface-carousel-content">
        {Array.from({ length: 8 }).map((_, index) => (
          <CarouselItem key={`${prefix}-skeleton-${index}`} className="issue-influence-surface-carousel-item basis-auto px-1">
              <LoadingSkeleton
                count={1}
                variant="entity-avatar"
                avatarSize="h-9 w-9"
              />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );

  const CAROUSEL_ROW_MIN_HEIGHT = "min-h-[120px]";

  const renderInfluenceCarousel = (
    cards: InfluenceCardData[],
    direction: InfluenceDirection,
    loading: boolean
  ) => {
    if (loading) {
      return (
        <div className={`issue-influence-surface-carousel-wrapper w-full ${CAROUSEL_ROW_MIN_HEIGHT} flex items-center`}>
          {renderLoadingCarousel(direction)}
        </div>
      );
    }

    if (cards.length === 0) {
      return (
        <div className={`issue-influence-surface-carousel-empty w-full ${CAROUSEL_ROW_MIN_HEIGHT} flex items-center justify-center`}>
          <EmptyState
            title={direction === "influencer" ? "No influencers yet" : "No influenced yet"}
            subtitle={direction === "influencer" ? "Be the first to add an issue that influences this one" : "Be the first to add an issue that is influenced by this one"}
            className="p-1"
          />
        </div>
      );
    }

    return (
      <div className={`issue-influence-surface-carousel-wrapper w-full ${CAROUSEL_ROW_MIN_HEIGHT} flex items-center`}>
        <Carousel
          className="issue-influence-surface-carousel w-full"
          opts={{ dragFree: true, align: "center" }}
        >
          <CarouselContent className="issue-influence-surface-carousel-content">
            {cards.map(({ compoundIssue, standaloneIssue }) => (
            <CarouselItem key={`${direction}-${compoundIssue.id}`} className="issue-influence-surface-carousel-item basis-auto px-1">
              <div className={`issue-influence-surface-item flex items-center gap-2 ${direction === "influencer" ? "flex-col-reverse" : "flex-col"}`}>
                <button
                  type="button"
                  className="issue-influence-surface-avatar-button flex items-center justify-center"
                  onClick={() => handleOpenCompoundIssue(compoundIssue)}
                  aria-label={`Open influence profile for ${standaloneIssue.name}`}
                >
                  <EntityAvatar
                    entityType="issue"
                    entity={standaloneIssue}
                    entityChip={true}
                    isInputField={false}
                    showLink={false}
                    isGeneralEntity={true}
                    showCloseChip={false}
                    className="max-w-[240px] pr-1"
                  />
                </button>
                <button
                  type="button"
                  className="issue-influence-surface-recenter-button flex items-center justify-center size-7 rounded-full bg-eoe-icon-hover dark:bg-eoe-dark-icon hover:bg-eoe-icon-stroke/20 dark:hover:bg-eoe-dark-icon-hover transition-colors"
                  onClick={() => handleRecenterIssue(standaloneIssue)}
                  aria-label={`Explore ${standaloneIssue.name} influences`}
                  title={`Explore ${standaloneIssue.name}`}
                >
                  <Network className="size-4 text-eoe-text dark:text-eoe-dark-text" />
                </button>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      </div>
    );
  };

  if (error) {
    return error.match(/404/)
      ? <NotFoundPage displayStyle="page" errorMessage="Sorry, we couldn't find this issue." buttonText="View Other Issues" buttonHref="/issues/new" />
      : <ServerErrorPage />;
  }

  const centerIssueNode = (
    <div className="issue-influence-surface-center w-full flex flex-col items-center justify-center gap-2 py-4">
      {isIssueLoading || !currentIssue ? (
        <>
          <LoadingSkeleton count={1} height="h-16" width="w-16" margin="m-0" rounded="rounded-full" containerClassName="flex" />
          <LoadingSkeleton count={1} height="h-4" width="w-36" margin="m-0" rounded="rounded-full" containerClassName="flex" />
          <LoadingSkeleton count={1} height="h-3" width="w-full" margin="mt-2" rounded="rounded-full" containerClassName="flex w-full " />
          <LoadingSkeleton count={1} height="h-3" width="w-full" margin="m-0" rounded="rounded-full" containerClassName="flex w-full " />
          <LoadingSkeleton count={1} height="h-3" width="w-[80%]" margin="m-0" rounded="rounded-full" containerClassName="flex w-full" />
        </>
      ) : (
        <>
          <button
            type="button"
            className="issue-influence-surface-center-trigger flex flex-col items-center gap-2 max-w-md"
            onClick={() => openIssueFeed(currentIssue.cname)}
            aria-label={`Open ${currentIssue.name} feed`}
          >
            <EntityAvatar
              entityType="issue"
              entity={currentIssue}
              displayMode="avatarOnly"
              hasRing={false}
              className="w-16 h-16"
            />
            <span className="issue-influence-surface-center-title text-center text-base font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading line-clamp-1">
              {currentIssue.name}
            </span>
            {centerIssueExtract && (
              <p className="issue-influence-surface-center-extract px-3 text-center text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub line-clamp-3">
                {centerIssueExtract}
              </p>
            )}
          </button>
          {currentIssue && (
            <div className="issue-influence-surface-center-actions w-full max-w-md px-2" onClick={(e) => e.stopPropagation()}>
              <EntityActions entity={currentIssue} entityType="issue" />
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderAddSection = (
    direction: InfluenceDirection,
    selected: Suggestion | null,
    onSelect: (s: Suggestion) => void,
    excludedIds: number[],
    onSubmit: () => void,
    isSubmitting: boolean,
    actionMessage: string | null
  ) => {
    const addButtonFirst = direction === "influencer";
    const addButton = (
      <AddButton
        tooltipTitle={`Add ${direction === "influencer" ? "influencer" : "influenced"}`}
        buttonText={direction === "influencer" ? "Influencer" : "Influenced"}
        onClick={onSubmit}
        isLoading={isSubmitting}
        className={`w-full flex-shrink-0 ${!selected && !isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
      />
    );
    const searchInput = (
      <div className="w-full">
        <IssueSearch
          inputType="add"
          placeholder={`Search ${direction === "influencer" ? "influencers" : "influenced"}`}
          excludeIssueIds={excludedIds}
          excludeCompoundIssues
          onSelect={onSelect}
          className="w-full"
          prepopulateData={selected}
        />
      </div>
    );
    return (
    <div className="issue-influence-surface-add-section w-full max-w-md mx-auto flex flex-col space-y-2 py-3 px-2">
      {addButtonFirst ? addButton : searchInput}
      {addButtonFirst ? searchInput : addButton}
      {actionMessage && (
        <div className={`text-sm flex items-center ${actionMessage.includes("success") ? "text-green-600 dark:text-green-400" : "text-warning-red dark:text-warning-red-dark"}`}>
          <Info className="mr-1 size-4" />
          <span>{actionMessage}</span>
        </div>
      )}
    </div>
    );
  };

  const content = (
    <div className="issue-influence-surface-content h-full min-h-full w-full px-2 py-6 flex flex-col justify-between">
      <section
        className="issue-influence-surface-top-carousel w-full flex justify-center [touch-action:pan-x]"
        data-vaul-no-drag
      >
        {renderInfluenceCarousel(influencerCards, "influencer", isInfluencersLoading)}
      </section>

      {currentIssue && renderAddSection(
        "influencer",
        selectedInfluencer,
        setSelectedInfluencer,
        getExcludedInfluencerIds(),
        handleAddInfluencer,
        isSubmittingInfluencer,
        actionMessageInfluencer
      )}

      {centerIssueNode}

      {currentIssue && renderAddSection(
        "influenced",
        selectedInfluenced,
        setSelectedInfluenced,
        getExcludedInfluencedIds(),
        handleAddInfluenced,
        isSubmittingInfluenced,
        actionMessageInfluenced
      )}

      <section
        className="issue-influence-surface-bottom-carousel w-full flex justify-center [touch-action:pan-x]"
        data-vaul-no-drag
      >
        {renderInfluenceCarousel(influencedCards, "influenced", isInfluencedLoading)}
      </section>
    </div>
  );

  if (isStacked) {
    return content;
  }

  return (
    <>
      {!userIsMobile ? (
        content
      ) : (
        <Drawer
          open={isDrawerOpen}
          onOpenChange={(open) => {
            setIsDrawerOpen(open);
            if (!open) {
              navigate(-1);
            }
          }}
          direction="right"
          shouldScaleBackground={false}
        >
          <DrawerContent
            hideHandle
            fullScreen
            className="!left-auto !right-0 w-full focus-visible:outline-none !rounded-none !border-none bg-eoe-bg dark:bg-eoe-dark-bg"
          >
            {content}
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export default IssueInfluenceSurface;
